import React, { useState, useRef, useEffect, useCallback } from "react";
import AgGridTable from "../utilities/AgGridTable";
import ListPageHeader from "../utilities/ListPageHeader";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Typography, Tab, Tabs, Tooltip } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import DailyExchangeRateForm from "./DailyExchangeRateForm";
import moment from "moment";
import AgCharts from "../utilities/AgCharts";
import { AgChart } from "ag-charts-community";
import CustomDatePicker from "../utilities/CustomDatePicker";

const DailyExchangeRate = () => {

    const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}${oAuthServiceConfig.dailyExchangeRate}`;
    const [selectedRows, setSelectedRows] = useState([]);
    const [rowData, setRowData] = useState();
    const perPage = 10;
    const [error, setError] = useState("")
    const gridRef = useRef();
    const chartRef = useRef();
    const childRef = useRef();
    const [snackState, setSnackState] = useState(false);
    const [successMsg, setSuccessMsg] = useState();
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [editId, setEditId] = useState();
    const [formOpen, setFormOpen] = useState(false);
    const [viewData, setViewData] = useState(false);
    const [status, setStatus] = useState();
    const [deleteDisable, setDeleteDisable] = useState(true);
    const [chartType, setChartType] = useState('line');
    const [tabValue, setTabValue] = useState(0)
    const handleDateFormat = (props) => {
        return props.value && moment(props.value).format('DD-MM-YYYY')
    }
    const [columnDefs, setColumnDefs] = useState([
        { field: 'date', enableRowGroup: true, cellRenderer: handleDateFormat    },
        { field: 'resourceMoneyUnit', enableRowGroup: true },
        { field: 'targetMoneyUnit', enableRowGroup: true },
        { field: 'euroRate', enableRowGroup: true },
        { field: 'calculated', enableRowGroup: true, headerName: "Calculated @" },
    ]);
    const [rateChartOptions, setRateChartOptions] = useState({
        data: [],
        series: [{
            type: 'line',
            xKey: 'date',
            yKey: 'rate',
            yName: 'rate',
            fillOpacity: 0.5,
        }],
        theme: 'ag-default-dark',
        autoSize: true,
        title: {
            text: `Rate Chart`,
        },
        legend: {
            position: 'bottom'
        },
    });

    const handleFormClose = () => {
        setFormOpen(false);
    }

    function decimalCount(number) {
        // Convert to String
        const numberAsString = number.toString();
        // String Contains Decimal
        if (numberAsString.includes('.')) {
            return numberAsString.split('.')[1].length;
        }
        // String Does Not Contain Decimal
        return 0;
    }

    const loadRates = async () => {
        try {
            const list = await OAuthService.getList(url + '?size=1000&sort=date,desc');
            let rates = list.data?._embedded?.dailyExchangeRates.map((data) => {
                if(decimalCount(Number(data.euroRate)) > 5 || (data.createdBy === 'SYSTEM' && !data.updatedBy)){
                    data.euroRate = Number(data.euroRate).toFixed(4);
                    data.calculated = Number(data.calculated).toFixed(4);
                }
                return data;
            });
            setRowData(rates);
            const chartdata = rates.map((drate) => {
                return { date: drate.date && moment(drate.date).format('DD-MM-YYYY'), rate: Number(drate.euroRate) }
            });
            setRateChartOptions((prevOptions) => ({
                ...prevOptions, data: chartdata
            }))
        } catch (error) {
            setError(error.message);
            console.log(error);
        }
    }

    const onGridReady = useCallback((params) => {
        loadRates();
        var columnToolPanel = gridRef.current.api.getToolPanelInstance('columns');
        columnToolPanel.expandColumnGroups();

    }, []);

    const onBtExport = useCallback(() => {
        const params = {
            fileName: 'DailyExchangeRates.xlsx',
            sheetName: 'Daily Exchange Rates'
        }
        gridRef.current.api.exportDataAsExcel(params);
    }, []);

    const handlePurge = (e) => {
        selectedRows.map(async (row) => {
            var id = row?.id || row?._links?.self?.href.split('/').pop();
            try {
                setSnackState(false)
                await OAuthService.deleteData(url, id);
                setSuccessMsg("Data deleted Successfully")
                setSnackState(true);
                loadRates();
            } catch (error) {
                console.log("eror", error);
                setError(error.message);
            }
        })
    };

    const onSelectionChanged = (params) => {
        setSelectedRows(params.api.getSelectedRows());
        setEditId('');
        setViewData(true)
    }

    useEffect(() => {
        setSelectedRows([])
    }, [rowData])

    const onRowDoubleClicked = (params) => {
        selectedRows.length > 0 && setEditId(selectedRows[0].id || selectedRows[0]?._links?.self?.href.split('/').pop())
        setFormOpen(true);
        setViewData(true)
    }

    useEffect(() => {
        if (selectedRows.length > 0) {
            setDeleteDisable(false)
        } else {
            setDeleteDisable(true)
        }
    }, [selectedRows]);

    const handleAddButton = () => {
        setFormOpen(true)
        setEditId('')
        setViewData(true)
    }

    const handleFromDateChange = async (value) => {
        const startDate = value === null ? null : moment(value).format('yyyy-MM-DD')
        setFromDate(startDate);
        if (startDate) {
            const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}api/${oAuthServiceConfig.dailyExchange}?size=1000&sort=date,desc`;
            let addObj = { startDate: startDate };
            if (toDate) {
                addObj = { ...addObj, endDate: toDate };
            }
            const result = await OAuthService.addData(url, addObj)
            setRowData(result.data ? result.data : [])
            const filterData = result?.data;
            const chartdata = filterData.map((drate) => {
                return { date: drate.date && moment(drate.date).format('DD-MM-YYYY'), rate: drate.euroRate }
            });
            setRateChartOptions((prevOptions) => ({
                ...prevOptions, data: chartdata
            }))
        } else if (toDate) {
            const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}api/${oAuthServiceConfig.dailyExchange}?size=1000&sort=date,desc`;
            let addObj = { endDate: toDate };
            const result = await OAuthService.addData(url, addObj)
            setRowData(result.data ? result.data : [])
            const filterData = result?.data;
            const chartdata = filterData.map((drate) => {
                return { date: drate.date && moment(drate.date).format('DD-MM-YYYY'), rate: drate.euroRate }
            });
            setRateChartOptions((prevOptions) => ({
                ...prevOptions, data: chartdata
            }))
        } else {
            loadRates()
        }
    }

    const handleToDateChange = async (value) => {
        const endDate = value === null ? null : moment(value).format('yyyy-MM-DD');
        setToDate(endDate);

        if (endDate) {
            const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}api/${oAuthServiceConfig.dailyExchange}?size=1000&sort=date,desc`;
            let addObj = { endDate: endDate };
            if (fromDate) {
                addObj = { ...addObj, startDate: fromDate };
            }
            const result = await OAuthService.addData(url, addObj)
            setRowData(result.data ? result.data : [])
            const filterData = result?.data;
            if (filterData) {
                const chartdata = filterData.map((drate) => {
                    return { date: drate.date && moment(drate.date).format('DD-MM-YYYY'), rate: drate.euroRate }
                });
                setRateChartOptions((prevOptions) => ({
                    ...prevOptions, data: chartdata
                }))
            }
        } else if (fromDate) {
            const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}api/${oAuthServiceConfig.dailyExchange}?size=1000&sort=date,desc`;
            let addObj = { startDate: fromDate };
            const result = await OAuthService.addData(url, addObj)
            setRowData(result.data ? result.data : [])
            const filterData = result?.data;
            if (filterData) {
                const chartdata = filterData.map((drate) => {
                    return { date: drate.date && moment(drate.date).format('DD-MM-YYYY'), rate: drate.euroRate }
                });
                setRateChartOptions((prevOptions) => ({
                    ...prevOptions, data: chartdata
                }))
            }
        } else {
            loadRates()
        }
    }

    const exchangeRateFilter = () => {
        return <>
            <label className="pt-6 min-w-fit">From Date:</label>
            <div className="grid">
                <CustomDatePicker
                    className="w-[150px]"
                    format={'dd-MM-yyyy'}
                    name={'fromDate'}
                    value={fromDate}
                    maxDate={toDate && new Date(toDate)}
                    handleChange={handleFromDateChange}
                    componentsProps={{
                        actionBar: { actions: ["clear"] },
                    }}
                />
            </div>
            <label className="pt-6 min-w-fit">To Date:</label>
            <div className="grid">
                <CustomDatePicker
                    className="w-[150px]"
                    format={'dd-MM-yyyy'}
                    name={'toDate'}
                    value={toDate}
                    minDate={fromDate && new Date(fromDate)}
                    handleChange={handleToDateChange}
                    componentsProps={{
                        actionBar: { actions: ["clear"] },
                    }}
                />
            </div>
            {selectedRows.length > 0 &&
            <Tooltip title={"Edit Exchange Rate"}>
            <Button
              variant="contained"
              color="warning"
              style={{ marginLeft: "3px" }}
              onClick={handleEditRate}
            >
              <FuseSvgIcon>heroicons-solid:pencil</FuseSvgIcon>
            </Button>
          </Tooltip>
            }
        </>
    }

    const handleEditRate = () => {
        setViewData(false)
        selectedRows.length > 0 && setEditId(selectedRows[0].id || selectedRows[0]?._links?.self?.href.split('/').pop())
        setFormOpen(true)
    }

    const handleSave = () => {
        const childelement = childRef.current;
        const submitData = childelement.handleSubmit();
        submitData.then((val) => {
            if (val) {
                setFormOpen(false);
                loadRates();
            }
        })
    }

    const handleEdit = () => {
        setViewData(false)
    }
    const checkedStatus = (st) => {
        setStatus(st)
    }
    const handleSuccessMsg = (msg) => {
        if (msg) {
            setSuccessMsg(msg)
            setSnackState(true)
        } else {
            setSnackState(false)
        }
    }

    useEffect(() => {
        checkedStatus();
        handleSuccessMsg();
    }, []);

    const handleTabChange = (event, value) => {
        setTabValue(value);
    }

    const handleChartTypeChange = (event) => {
        setChartType(event.target.value)
        if (event.target.value === 'pie') {
            setRateChartOptions((prevOptions) => ({
                ...prevOptions,
                series: [{
                    type: event.target.value,
                    labelKey: 'date',
                    angleKey: 'rate',
                    fillOpacity: 0.5,
                }],
            }))
        } else {
            setRateChartOptions((prevOptions) => ({
                ...prevOptions,
                series: [{
                    type: event.target.value,
                    xKey: 'date',
                    yKey: 'rate',
                    yName: 'rate',
                    // fillOpacity: 0.5,
                }],
            }))
        }
    }

    const downloadChart = useCallback(() => {
        chartRef?.current?.chart && AgChart.download(chartRef?.current?.chart);
    }, []);

    return (
        <>
            <ListPageHeader
                componentName="Daily Exchange Rate"
                onBtExport={onBtExport}
                handlePurge={handlePurge}
                handleAddButton={handleAddButton}
                pageName="Daily Exchange Rates"
                headerInputFields={exchangeRateFilter()}
                error={error}
                deleteDisable={deleteDisable}
                successMsg={successMsg}
                gridRef={gridRef}
                snackState={snackState}
            />
            <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="secondary"
          textColor="inherit"
          variant="scrollable"
          scrollButtons="auto"
          classes={{ root: 'w-full border-b-1' }}
        >
          <Tab 
            label="List" 
            sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'text.primary' } }}
          />
          <Tab 
            label="Rate Visualization" 
            sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'text.primary' } }}
          />
        </Tabs>
            <div className={tabValue !== 0 ? 'hidden' : 'h-full'}>
                <AgGridTable
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                    onSelectionChanged={onSelectionChanged}
                    onRowDoubleClicked={onRowDoubleClicked}
                    gridRef={gridRef}
                    editable={false}
                    paginationPageSize={perPage}
                    cacheBlockSize={perPage}
                    saveGridStateName={'dailyExchangeRates'}
                />
            </div>

            <div className={tabValue !== 1 ? 'hidden' : 'h-full'}>
                <div className="flex flex-col sm:flex-row space-y-16 sm:space-y-0 flex-1 w-full items-center justify-between pt-10">
                    <div className="flex flex-row items-center justify-between space-x-16" >
                        <Typography>Chart Type: </Typography>
                        <Select className="min-w-[90px]" size="small" onChange={handleChartTypeChange} value={chartType}>
                            <MenuItem value='pie'>Pie</MenuItem>
                            <MenuItem value='column'>Column</MenuItem>
                            <MenuItem value='line'>Line</MenuItem>
                            <MenuItem value='area'>Area</MenuItem>
                            {/* <MenuItem value='scatter'>Scatter</MenuItem>
                                    <MenuItem value='histogram'>Histogram</MenuItem> */}
                        </Select>
                    </div>
                    <Button
                        variant="contained"
                        color="warning"
                        className="mb-10"
                        onClick={downloadChart}
                    >
                        <FuseSvgIcon>heroicons-solid:download</FuseSvgIcon>
                    </Button>
                </div>
                <div className="h-auto">
                    <AgCharts
                        chartRef={chartRef}
                        options={rateChartOptions}
                    />
                </div>
            </div>



            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                onClose={handleFormClose}
                open={formOpen}
            // {...other}
            >
                <DialogTitle>
                    <div className="flex flex-col sm:flex-row space-y-16 sm:space-y-0">
                        <span>{editId ? viewData ? 'View' : 'Update' : 'Add'} Daily Exchange Rate</span>
                        <div className="flex flex-col w-full sm:w-auto sm:flex-row space-y-16 sm:space-y-0 flex-1 items-center justify-end space-x-8">
                            <Button onClick={handleFormClose}><FuseSvgIcon>heroicons-solid:x</FuseSvgIcon></Button>
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent dividers>
                    <DailyExchangeRateForm id={editId} ref={childRef} viewData={editId && viewData} checkedStatus={checkedStatus} successMsg={handleSuccessMsg} />
                </DialogContent>
                <DialogActions>
                    {(editId && viewData) ? <Button
                        variant="contained"
                        color="warning"
                        onClick={handleEdit}
                    >
                        <FuseSvgIcon>heroicons-solid:pencil</FuseSvgIcon>
                    </Button> :
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleSave}
                            disabled={status}
                        >
                            <FuseSvgIcon>heroicons-solid:document</FuseSvgIcon>
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </>
    )
}

export default DailyExchangeRate;
